import { useQuery } from '@w/react-query'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'

/**
 * page-views.spec.md "Showing views": the page's past viewers for readers who may see views (Edit+ or admin), fetched with the
 * page and shown in the top bar's avatar stack (`PagePeople`); there is no live update. `enabled` is false when no other
 * member can access the page, so a private page sends no query.
 */
export function usePageViews({ workspaceId, pageId, enabled }: { workspaceId: string; pageId: string; enabled: boolean }) {
  const ctx = useRouterContext()
  const { data } = useQuery({
    queryKey: ['page-views', workspaceId, pageId],
    // flashcards-local-first.spec.md: the anonymous principal's workspace has no other member, so `enabled` is false there.
    queryFn: () => ctx.tel.span({ name: 'page.views', target: 'job', input: { workspaceId, pageId } }, async tel => {
      const rows = []
      let cursor: {viewedAt:number;userId:string} | undefined
      for (;;) {
        const page = await ctx.rpcClient.page.views.query({ workspaceId, pageId, limit:50, cursor }, { tel })
        rows.push(...page)
        if (page.length<50) return rows
        cursor=page.at(-1)
      }
    }),
    staleTime: 60_000,
    retry: false,
    enabled,
  }, ctx.queryClient)
  return data
}

/** Past viewers of a page, newest first, without the caller (page-views.spec.md "Access"). */
export type PageViewRows = NonNullable<ReturnType<typeof usePageViews>>
